import { useCallback, useMemo } from 'react'
import { useForm, UseFormProps, UseFormReturn, FieldValues } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { SchemaOf, BaseSchema, ObjectSchema, ArraySchema } from 'yup'
import { AnyObject, Maybe } from 'yup/lib/types'
import Lazy from 'yup/lib/Lazy'

import { useFormInputRegister } from './useFormInputRegister'

type ApiErrorType = {
  details?: unknown
}

type HookReturn = {
  setApiError: (apiError?: ApiErrorType) => void
}

// Copy pasted from yup because it is not exported
export type ObjectSchemaOf<T extends AnyObject> = ObjectSchema<{
  [k in keyof T]-?: T[k] extends Array<infer E>
    ? ArraySchema<SchemaOf<E> | Lazy<SchemaOf<E>>>
    : T[k] extends AnyObject
    ? ObjectSchemaOf<T[k]> | ObjectSchemaOf<Lazy<T[k]>>
    : BaseSchema<Maybe<T[k]>, AnyObject, T[k]>
}>

export function useYupForm<TValues extends FieldValues, TContext extends object = object>(
  schema: ObjectSchemaOf<TValues>,
  formProps?: UseFormProps<TValues, TContext>,
): UseFormReturn<TValues, TContext> & HookReturn {
  const methods = useForm<TValues, TContext>({
    mode: 'all',
    ...formProps,
    resolver: yupResolver<any>(schema),
  })

  const setApiError = useCallback(
    (apiError?: ApiErrorType) => {
      if (apiError == null) {
        // reset error
        methods.clearErrors('global' as any)
      } else {
        // set errors by name to show under input
        if (typeof apiError.details === 'object') {
          Object.entries(apiError.details as any).forEach(([key, err]) => {
            methods.setError(key as any, {
              type: 'apiError',
              message: (err as any).toString(),
            })
          })
        }

        // set global message error with all errors
        methods.setError('global' as any, {
          type: 'apiError',
          message: apiError.toString(),
        })
      }
    },
    [methods],
  )

  const register = useFormInputRegister<TValues, TContext>(methods, schema)

  return useMemo(
    () => ({
      ...methods,
      setApiError,
      register,
    }),
    [methods, register, setApiError],
  )
}
